import axios from 'axios';

// Read from the environment instead of hardcoding, so switching domains
// (or environments - local/staging/prod) only ever requires updating
// .env / your deploy platform's env vars, never this file.
// Falls back to the old Railway URL only if the env var is somehow
// missing, so a misconfigured deploy fails loudly via network errors
// rather than silently building with an undefined baseURL.
const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || "https://backend-production-2c34.up.railway.app";

// Derive the WebSocket URL from the same source of truth instead of
// maintaining it as a second hardcoded string that can drift out of
// sync with BACKEND_BASE_URL. https:// -> wss://, http:// -> ws://
const WS_BASE_URL = BACKEND_BASE_URL.replace(/^https:\/\//, "wss://").replace(/^http:\/\//, "ws://");

const api = axios.create({
    baseURL: BACKEND_BASE_URL,
    headers: {
        "Content-Type": "application/json",
    }
});

// 🛡️ Attach YOUR backend's JWT token (saved during login)
api.interceptors.request.use((config) => {
    const token = localStorage.getItem("token");

    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
}, (error) => {
    return Promise.reject(error);
});

// 🔒 Centralized session handling: if the token is expired/invalid,
// clear it and send the user to login — in ONE place, so every page
// doesn't need its own copy of this logic (and can't disagree with
// App.js about what to do next).
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            const alreadyOnLogin = window.location.pathname === "/login";

            // Wipe the stale session so App.js's own boot-time check
            // ("if token exists, redirect to home") doesn't immediately
            // bounce the user right back here.
            localStorage.removeItem("token");
            localStorage.removeItem("role");
            localStorage.removeItem("user_id");

            if (!alreadyOnLogin) {
                window.location.href = "/login";
            }
        }

        return Promise.reject(error);
    }
);

// 🖼️ Centralized image URL resolver
// Handles both:
//   - Legacy local uploads e.g. "/uploads/xyz.jpg" -> prefixed with backend host
//   - Firebase (or any already-absolute) URLs e.g. "https://storage.googleapis.com/..." -> used as-is
// Use this EVERYWHERE an item.image_url is rendered, instead of hardcoding the host.
export function getImageUrl(imagePath) {
    if (!imagePath) return null;

    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
        return imagePath;
    }

    return `${BACKEND_BASE_URL}${imagePath}`;
}

export default api;
export { WS_BASE_URL };